const PATHS = Object.freeze({
  REPORT: "/district-school-insights/advanced-reporting",
  SCHEDULED_REPORTS: "/district-school-insights/scheduled-reports"
})

const ADVANCE_REPORTING_ADMIN_ROLES = ["superadmin", "admin"]

const DEFAULT_CHART_IDS = Object.freeze({
  USERS_KPI: "usersKpi",
  SCHOOL_YEAR_PASS_BREAKDOWN: "schoolYearPassBreakdown",
  SCHOOL_YEAR_PASS_TREND: "schoolYearPassTrend",
  PASS_BREAKDOWN: "passBreakdown",
  PASS_TREND: "passTrend",
  TOTAL_PASSES_BY_SCHOOLS: "totalPassesBySchools",
  MOST_PASSES_TAKEN: "mostPassesTaken",
  MOST_PASSES_GRANTED: "mostPassesGranted",
  PASS_BREAKDOWN_BY_GRAD_YEAR: "passBreakdownByGraduationYear",
  AVERAGE_PASS_DURATION: "averagePassDuration",
  MOST_FREQUENTED_LOCATIONS: "mostFrequentedLocations",
  TARDY_INFRACTIONS: "tardyInfractions",
  CONTACT_CONTROL: "contactControl",
  VISITORS_TREND: "visitorsTrend",
  ZERO_GRANTERS: "zeroGranters"
})

const DATE_RANGE_TYPES = Object.freeze({
  YESTERDAY: "previous_day",
  THIS_WEEK: "this_week",
  THIS_MONTH: "this_month",
  THIS_YEAR: "this_year",
  CUSTOM: "custom" // Note: this key is important for showing custom date range in the date filter
})

const CHART_BE_NAME_VS_ID = Object.freeze({
  KPI: DEFAULT_CHART_IDS.USERS_KPI,
  "School year pass breakdown": DEFAULT_CHART_IDS.SCHOOL_YEAR_PASS_BREAKDOWN,
  "School year pass trend chart": DEFAULT_CHART_IDS.SCHOOL_YEAR_PASS_TREND,
  "Pass breakdown": DEFAULT_CHART_IDS.PASS_BREAKDOWN,
  "Pass trend chart": DEFAULT_CHART_IDS.PASS_TREND,
  "Total passes granted by schools": DEFAULT_CHART_IDS.TOTAL_PASSES_BY_SCHOOLS,
  "Most passes taken": DEFAULT_CHART_IDS.MOST_PASSES_TAKEN,
  "Most passes granted": DEFAULT_CHART_IDS.MOST_PASSES_GRANTED,
  "Pass breakdown by grad year": DEFAULT_CHART_IDS.PASS_BREAKDOWN_BY_GRAD_YEAR,
  "Average pass duration": DEFAULT_CHART_IDS.AVERAGE_PASS_DURATION,
  "Most frequented locations": DEFAULT_CHART_IDS.MOST_FREQUENTED_LOCATIONS,
  "Tardy infractions": DEFAULT_CHART_IDS.TARDY_INFRACTIONS,
  "Contact control": DEFAULT_CHART_IDS.CONTACT_CONTROL,
  "Visitor trend chart": DEFAULT_CHART_IDS.VISITORS_TREND,
  "Zero granters": DEFAULT_CHART_IDS.ZERO_GRANTERS
})

const CHART_ID_VS_TITLE = Object.freeze({
  [DEFAULT_CHART_IDS.USERS_KPI]: "User Totals and Login Activity",
  [DEFAULT_CHART_IDS.SCHOOL_YEAR_PASS_BREAKDOWN]: "School Year Pass Breakdown",
  [DEFAULT_CHART_IDS.SCHOOL_YEAR_PASS_TREND]: "School Year Pass Trend",
  [DEFAULT_CHART_IDS.PASS_BREAKDOWN]: "Pass Breakdown",
  [DEFAULT_CHART_IDS.PASS_TREND]: "Pass Trend",
  [DEFAULT_CHART_IDS.TOTAL_PASSES_BY_SCHOOLS]: "Most Passes Granted By Schools",
  [DEFAULT_CHART_IDS.MOST_PASSES_TAKEN]: "Most Passes Taken",
  [DEFAULT_CHART_IDS.MOST_PASSES_GRANTED]: "Most Passes Granted",
  [DEFAULT_CHART_IDS.PASS_BREAKDOWN_BY_GRAD_YEAR]:
    "Pass Breakdown by Graduation Year",
  [DEFAULT_CHART_IDS.AVERAGE_PASS_DURATION]: "Average Pass Duration",
  [DEFAULT_CHART_IDS.MOST_FREQUENTED_LOCATIONS]: "Most Frequented Locations",
  [DEFAULT_CHART_IDS.TARDY_INFRACTIONS]: "Tardy Infractions",
  [DEFAULT_CHART_IDS.CONTACT_CONTROL]: "Contact Control",
  [DEFAULT_CHART_IDS.VISITORS_TREND]: "Visitors Trend",
  [DEFAULT_CHART_IDS.ZERO_GRANTERS]: "Zero Granters"
})

const CHART_ID_VS_INFORMATION = Object.freeze({
  [DEFAULT_CHART_IDS.USERS_KPI]:
    "Shows the total number of users by role, and percentage who have logged in.",
  [DEFAULT_CHART_IDS.SCHOOL_YEAR_PASS_BREAKDOWN]:
    "Shows frequency of pass type usage in relation to one another since July 1st.",
  [DEFAULT_CHART_IDS.SCHOOL_YEAR_PASS_TREND]:
    "Compares pass usage month by month, this year versus last year.",
  [DEFAULT_CHART_IDS.PASS_BREAKDOWN]:
    "Shows how different pass types are being used for the selected time period.",
  [DEFAULT_CHART_IDS.PASS_TREND]:
    "Compares pass activity for the selected time period.",
  [DEFAULT_CHART_IDS.TOTAL_PASSES_BY_SCHOOLS]:
    "Compares pass usage across the top ten schools in the district.",
  [DEFAULT_CHART_IDS.MOST_PASSES_TAKEN]:
    "Shows which students are taking the most passes.",
  [DEFAULT_CHART_IDS.MOST_PASSES_GRANTED]:
    "Shows which adults are interacting the most passes.",
  [DEFAULT_CHART_IDS.PASS_BREAKDOWN_BY_GRAD_YEAR]:
    "Shows pass usage by graduation year.",
  [DEFAULT_CHART_IDS.AVERAGE_PASS_DURATION]:
    "Shows the average amount of time spent on each pass type.",
  [DEFAULT_CHART_IDS.MOST_FREQUENTED_LOCATIONS]:
    "Shows which locations students visit most often.",
  [DEFAULT_CHART_IDS.TARDY_INFRACTIONS]:
    "Tracks total number of tardy infractions for the selected time period.",
  [DEFAULT_CHART_IDS.CONTACT_CONTROL]:
    "Shows top prevented interactions between students who have active Contact Control for the selected time period.",
  [DEFAULT_CHART_IDS.VISITORS_TREND]:
    "Tracks the number of visitors for the selected time period.",
  [DEFAULT_CHART_IDS.ZERO_GRANTERS]:
    "Shows adults who have not granted any passes for the selected time period."
})

const REPORT_FORMATS = Object.freeze({
  CSV: "csv",
  PDF: "pdf"
})

const REPORT_FORMAT_OPTIONS = Object.freeze([
  {
    id: REPORT_FORMATS.CSV,
    name: "CSV",
    active: true
  },
  {
    id: REPORT_FORMATS.PDF,
    name: "PDF (charts)",
    active: false
  }
])

const REPETITION_OPTIONS = Object.freeze([
  {
    id: "daily",
    name: "Daily",
    active: true
  },
  {
    id: "weekly",
    name: "Weekly",
    active: true
  },
  {
    id: "monthly",
    name: "Monthly",
    active: true
  }
])

const END_OPTIONS = Object.freeze([
  {
    id: "on",
    name: "On",
    active: true
  },
  {
    id: "after",
    name: "After",
    active: true
  }
])

const NON_CONFIGURABLE_CHART_IDS = [
  DEFAULT_CHART_IDS.USERS_KPI,
  DEFAULT_CHART_IDS.SCHOOL_YEAR_PASS_BREAKDOWN,
  DEFAULT_CHART_IDS.SCHOOL_YEAR_PASS_TREND
]

const CONFIGURABLE_CHART_IDS = [
  DEFAULT_CHART_IDS.PASS_BREAKDOWN,
  DEFAULT_CHART_IDS.PASS_TREND,
  DEFAULT_CHART_IDS.TOTAL_PASSES_BY_SCHOOLS,
  DEFAULT_CHART_IDS.MOST_PASSES_TAKEN,
  DEFAULT_CHART_IDS.MOST_PASSES_GRANTED,
  DEFAULT_CHART_IDS.PASS_BREAKDOWN_BY_GRAD_YEAR,
  DEFAULT_CHART_IDS.AVERAGE_PASS_DURATION,
  DEFAULT_CHART_IDS.MOST_FREQUENTED_LOCATIONS,
  DEFAULT_CHART_IDS.TARDY_INFRACTIONS,
  DEFAULT_CHART_IDS.CONTACT_CONTROL,
  DEFAULT_CHART_IDS.VISITORS_TREND,
  DEFAULT_CHART_IDS.ZERO_GRANTERS
]

const PASS_TYPE_KEY_TO_NAME = Object.freeze({
  student: "Student",
  admin: "Admin",
  teacher: "Teacher",
  appointment: "Appointment",
  student_late_pass: "Student Late Pass",
  kiosk: "Kiosk"
})

const PASS_TYPE_ORDER = Object.freeze([
  "Student",
  "Kiosk",
  "Teacher",
  "Appointment",
  "Student Late Pass",
  "Admin"
])

// Advance Reporting Feature Flag - comma-separated school IDs (empty = all schools)
const ADVANCE_REPORTING_ALLOWED_SCHOOL_IDS =
  "5306,5360,5361,5362,5363,5364,5365,5366,5570,5603,5700,5738,5776,5806,5817,5885,5972,5973,6135,6136,6139,6140,6152,6156,6223,6289,6449,6450,6451,6499,6500,6563,6564,6565,6566,6567,6568,6569,6611,6612,6613,7386,7389,7449,7534,7556,7643,7648,7690,7921,7962,7994,8003,8004,8005,8026,8171,8215,8231,8232,8258,8306,8307,8489,8490,8493,8540,8561,8596,8611,8612,8613,8618,8630,8631,8633,1333,1351,1389,1406,1408,1411,2948,3055,3221,3303,3673,3674,3774,3931,4089,4715,5514,5775,6253,7312,7420,8098,8111,8304,8305,8497,1386,1404,1405,1407,1412,1413,1435,1523,1972,2193,2249,2293,2310,2455,2456,2479,2495,2611,2949,2951,2952,3349,3518,3587,3592,4199,4622,4992,5011,5015,5018,5096,5515,5688,5689,5690,5691,5748,6703,6870,7411,7622,8577"

export default {
  PATHS,
  ADVANCE_REPORTING_ADMIN_ROLES,
  DEFAULT_CHART_IDS,
  DATE_RANGE_TYPES,
  CHART_BE_NAME_VS_ID,
  CHART_ID_VS_TITLE,
  CHART_ID_VS_INFORMATION,
  REPORT_FORMATS,
  REPORT_FORMAT_OPTIONS,
  REPETITION_OPTIONS,
  END_OPTIONS,
  NON_CONFIGURABLE_CHART_IDS,
  CONFIGURABLE_CHART_IDS,
  PASS_TYPE_KEY_TO_NAME,
  PASS_TYPE_ORDER,
  ADVANCE_REPORTING_ALLOWED_SCHOOL_IDS
}
